import * as yup from 'yup';
import type { AgentConfigFormValues } from '../../../../types/features/storeDetails/overview';

export const DEFAULT_AGENT_CONFIG_FORM_VALUES: AgentConfigFormValues = {
    Heartbeat: { IntervalMinutes: 1 },
    ScannerProxy: { Enabled: true, AutoDetect: true, Port: '', BaudRate: null },
    PosDataUpload: { Enabled: false },
    PosDataDownload: { Enabled: false },
    posConfig: { ReadPath: '', WritePath: '', IncludeSubdirectories: true, EligibleExtensions: [] },
    NetworkShare: { Root: '', User: '', Password: '' },
    Verifone: {
        BaseUrl: '',
        User: '',
        Password: '',
        UseCompressed: false,
        AllowInsecure: true,
        NoProxy: true,
        TimeoutSeconds: 60,
    },
};

export const normalizePosTransferEnabled = (
    enabled: boolean | null | undefined,
    path: string | null | undefined,
): boolean => Boolean(enabled ?? true) && Boolean(path?.trim());

export const agentConfigSchema: yup.ObjectSchema<AgentConfigFormValues> = yup.object({
    Heartbeat: yup.object({
        IntervalMinutes: yup.number()
            .nullable()
            .defined()
            .typeError('Heartbeat interval must be a whole number of minutes')
            .when('$requiresHeartbeat', {
                is: (requiresHeartbeat?: boolean) => requiresHeartbeat !== false,
                then: (schema) => schema
                    .integer('Heartbeat interval must be a whole number of minutes')
                    .min(0, 'Heartbeat interval cannot be negative')
                    .required('Heartbeat interval is required'),
            }),
    }).required(),
    ScannerProxy: yup.object({
        Enabled: yup.boolean().required(),
        AutoDetect: yup.boolean().optional(),
        Port: yup.string().nullable().defined().when('AutoDetect', {
            is: false,
            then: (schema) => schema.test(
                'manual-scanner-proxy-port-required',
                'Port is required when auto detect is off',
                (value) => Boolean(value?.trim()),
            ),
        }),
        BaudRate: yup.number()
            .transform((value, originalValue) => (originalValue === '' || originalValue === null ? null : value))
            .nullable()
            .optional()
            .when('AutoDetect', {
                is: false,
                then: (schema) => schema
                    .typeError('Baud rate must be a number')
                    .required('Baud rate is required when auto detect is off')
                    .min(1, 'Baud rate must be at least 1'),
            }),
    }).required(),
    PosDataUpload: yup.object({
        Enabled: yup.boolean().required(),
    }).required(),
    PosDataDownload: yup.object({
        Enabled: yup.boolean().required(),
    }).required(),
    posConfig: yup.object({
        ReadPath: yup.string().nullable().defined(),
        WritePath: yup.string().nullable().defined(),
        IncludeSubdirectories: yup.boolean().required(),
        EligibleExtensions: yup.array().of(yup.string().required()).required(),
    }).required(),
    NetworkShare: yup.object({
        Root: yup.string().nullable().defined(),
        User: yup.string().nullable().defined(),
        Password: yup.string().nullable().defined(),
    }).nullable().defined(),
    Verifone: yup.object({
        BaseUrl: yup.string().nullable().defined(),
        User: yup.string().nullable().defined(),
        Password: yup.string().nullable().defined(),
        UseCompressed: yup.boolean().required(),
        AllowInsecure: yup.boolean().required(),
        NoProxy: yup.boolean().required(),
        TimeoutSeconds: yup.number().typeError('Timeout must be a number').min(5).max(300).required(),
    }).required(),
});
